import React from "react";
import {
  Calendar,
  Clock,
  DollarSign,
  Package,
  PawPrint,
  User,
  UserCheck,
  X,
} from "lucide-react";

interface ServiceDrawerProps {
  data: {
    isOpen: boolean;
    selectedDate: Date | null;
    services: any[];
  };
  handlers: {
    onClose: () => void;
    onServiceClick: (service: any) => void;
  };
}

const ServiceDrawer: React.FC<ServiceDrawerProps> = ({ data, handlers }) => {
  const { isOpen, selectedDate, services } = data;

  if (!isOpen) return null;

  const formattedDate = selectedDate
    ? selectedDate.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

  const getStatusBadge = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
      case "completed":
        return "bg-emerald-100 text-emerald-800 border-emerald-300";
      case "inprogress":
      case "in-progress":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "cancelled":
        return "bg-rose-100 text-rose-800 border-rose-300";
      default:
        return "bg-amber-100 text-amber-800 border-amber-300";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex justify-end">
      <div className="bg-white w-full max-w-md h-full shadow-2xl flex flex-col justify-between border-l border-slate-100 animate-in slide-in-from-right duration-200">
        {/* Drawer Header */}
        <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-100/60 rounded-2xl border border-emerald-200/60 text-emerald-800">
              <Calendar className="w-6 h-6 stroke-[2.2]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">{formattedDate}</h2>
              <p className="text-xs text-slate-500 font-semibold mt-0.5">
                {services.length} Scheduled Service{services.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <button
            onClick={handlers.onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded-full transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Content / Bookings List */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {services.length > 0 ? (
            services.map((service, index) => {
              const bId = service.bookingId || service.id || index;

              // Safe time extraction
              const startTime = service.startTime || service.serviceTime || "";
              const endTime = service.endTime || "";
              const timeDisplay =
                startTime && endTime
                  ? `${startTime} - ${endTime}`
                  : startTime || "Time Not Set";

              // Safe price extraction
              const priceDisplay =
                service.finalPrice ??
                service.salePrice ??
                service.price ??
                service.totalAmount ??
                0;

              return (
                <div
                  key={bId}
                  onClick={() => handlers.onServiceClick(service)}
                  className="bg-white border border-slate-200 hover:border-violet-300 rounded-2xl p-4 shadow-2xs hover:shadow-md transition cursor-pointer space-y-3 group relative"
                >
                  {/* Status Badge */}
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-slate-400">
                      #{service.bookingNumber || bId}
                    </span>
                    <span
                      className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold border ${getStatusBadge(
                        service.status
                      )}`}
                    >
                      {service.status || "Pending"}
                    </span>
                  </div>

                  {/* Customer Info */}
                  <div className="space-y-1.5 text-xs text-slate-700">
                    <div className="flex items-center gap-2 font-bold text-slate-800">
                      <User className="w-4 h-4 text-violet-600 shrink-0" />
                      <span>{service.customerName || service.userName || "N/A"}</span>
                    </div>

                    <div className="flex items-center gap-2 font-semibold text-slate-600">
                      <PawPrint className="w-4 h-4 text-slate-400 shrink-0" />
                      <span>{service.petName || "N/A"}</span>
                      {service.packageName && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span className="text-slate-500">{service.packageName}</span>
                        </>
                      )}
                    </div>

                    {/* Time Row */}
                    <div className="flex items-center gap-2 font-semibold text-slate-700">
                      <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                      <span>{timeDisplay}</span>
                      {service.durationMinutes > 0 && (
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.2 rounded font-bold">
                          ({service.durationMinutes} mins)
                        </span>
                      )}
                    </div>

                    {/* Price Row */}
                    <div className="flex items-center gap-2 font-bold text-slate-900">
                      <DollarSign className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>AED {priceDisplay}</span>
                    </div>

                    {/* Employee Assignment */}
                    {service.employeeName && (
                      <div className="flex items-center gap-2 text-[11px] text-emerald-700 font-semibold pt-1 border-t border-slate-100">
                        <UserCheck className="w-3.5 h-3.5 shrink-0" />
                        <span>Staff: {service.employeeName}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 space-y-2 py-20">
              <Package className="w-10 h-10 stroke-[1.5] text-slate-300" />
              <p className="text-sm font-semibold">No bookings scheduled for this date.</p>
            </div>
          )}
        </div>

        {/* Drawer Footer */}
       
      </div>
    </div>
  );
};

export default ServiceDrawer;